<template>
  <div class="ckediorContainer">
    <ckeditor
      :editor="CKEditorEditor"
      :value="ckeditorTextarea"
      @input="ckeditorTextarea = $event"
      :config="CKEditorConfig"
      :placeholder="placeholder"
      :is-read-only="isReadOnly"
    />
    <textarea
      :name="inputName"
      :value="ckeditorTextarea"
      style="display: none"
    />
    <template v-if="inputNamePic">
      <template v-for="(pic, key) in picObjects">
        <input
          v-if="pic.id"
          :key="key"
          type="hidden"
          :name="inputNamePic + '[]'"
          :value="pic.id"
        />
      </template>
    </template>
  </div>
</template>

<script>
import { HtmlEditorPro } from 'Vue/Common/HtmlEditor/HtmlEditors';
import '@ckeditor/ckeditor5-build-classic/build/translations/zh';
import EssentialsPlugin from '@ckeditor/ckeditor5-essentials/src/essentials';
import ParagraphPlugin from '@ckeditor/ckeditor5-paragraph/src/paragraph';
import BoldPlugin from '@ckeditor/ckeditor5-basic-styles/src/bold';
import ItalicPlugin from '@ckeditor/ckeditor5-basic-styles/src/italic';
import AutoformatPlugin from '@ckeditor/ckeditor5-autoformat/src/autoformat';
import Font from '@ckeditor/ckeditor5-font/src/font';
import ListPlugin from '@ckeditor/ckeditor5-list/src/list';
import TextTransformationPlugin from '@ckeditor/ckeditor5-typing/src/texttransformation';
import PasteFromOffice from '@ckeditor/ckeditor5-paste-from-office/src/pastefromoffice';
import Indent from '@ckeditor/ckeditor5-indent/src/indent';
import IndentBlock from '@ckeditor/ckeditor5-indent/src/indentblock';
import UnderlinePlugin from '@ckeditor/ckeditor5-basic-styles/src/underline';

import Image from '@ckeditor/ckeditor5-image/src/image';
import ImageUpload from '@ckeditor/ckeditor5-image/src/imageupload';
import ImageToolbar from '@ckeditor/ckeditor5-image/src/imagetoolbar';
// import ImageCaption from '@ckeditor/ckeditor5-image/src/imagecaption';
import ImageStyle from '@ckeditor/ckeditor5-image/src/imagestyle';
import ImageResize from '@ckeditor/ckeditor5-image/src/imageresize';
import LinkImage from '@ckeditor/ckeditor5-link/src/linkimage';

import LinkPlugin from '@ckeditor/ckeditor5-link/src/link';
import MediaEmbed from '@ckeditor/ckeditor5-media-embed/src/mediaembed';
import Alignment from '@ckeditor/ckeditor5-alignment/src/alignment';
import Table from '@ckeditor/ckeditor5-table/src/table';
import TableToolbar from '@ckeditor/ckeditor5-table/src/tabletoolbar';
import SimpleUploadAdapter from '@ckeditor/ckeditor5-upload/src/adapters/simpleuploadadapter';
// import InsertImagePlugin from 'FF/Vue/Common/HtmlEditor/InsertImagePlugin';

import HeadingPlugin from '@ckeditor/ckeditor5-heading/src/heading';
import StrikethroughPlugin from '@ckeditor/ckeditor5-basic-styles/src/strikethrough';
import Code from '@ckeditor/ckeditor5-basic-styles/src/code';
import SubscriptPlugin from '@ckeditor/ckeditor5-basic-styles/src/subscript';
import SuperscriptPlugin from '@ckeditor/ckeditor5-basic-styles/src/superscript';
import TodoList from '@ckeditor/ckeditor5-list/src/todolist';
import RemoveFormat from '@ckeditor/ckeditor5-remove-format/src/removeformat';
import CodeBlock from '@ckeditor/ckeditor5-code-block/src/codeblock';
import HorizontalLine from '@ckeditor/ckeditor5-horizontal-line/src/horizontalline';
import BlockQuote from '@ckeditor/ckeditor5-block-quote/src/blockquote';
import Undo from '@ckeditor/ckeditor5-undo/src/undo';

import FullScreen from 'Vue/Common/HtmlEditor/Plugins/ckeditor5-full-screen/FullScreen';
import HtmlSourceCode from 'Vue/Common/HtmlEditor/Plugins/ckeditor5-html-source-code/HtmlSourceCode';

export default {
  props: [
    'inputName',
    'value',
    'readonly',
    'placeholder',
    'inputNamePic',
    'textPics'
  ],
  data: () => {
    return {
      CKEditorEditor: HtmlEditorPro,
      isReadOnly: false,
      CKEditorConfig: {
        plugins: [
          EssentialsPlugin,
          ParagraphPlugin,
          BoldPlugin,
          ItalicPlugin,
          Font,
          UnderlinePlugin,
          AutoformatPlugin,
          PasteFromOffice,
          ListPlugin,
          Indent,
          IndentBlock,
          TextTransformationPlugin,
          Image,
          ImageUpload,
          ImageToolbar,
          // ImageCaption,
          ImageStyle,
          ImageResize,
          LinkImage,
          LinkPlugin,
          MediaEmbed,
          Alignment,
          Table,
          TableToolbar,
          SimpleUploadAdapter,
          // InsertImagePlugin,
          HeadingPlugin,
          StrikethroughPlugin,
          Code,
          SubscriptPlugin,
          SuperscriptPlugin,
          TodoList,
          RemoveFormat,
          CodeBlock,
          HorizontalLine,
          BlockQuote,
          FullScreen,
          HtmlSourceCode,
          Undo,
        ],
        toolbar: {
          items: [
            'heading',
            'bold',
            'italic',
            'underline',
            'strikethrough',
            'code',
            'subscript',
            'superscript',
            'removeFormat',
            '|',
            'fontSize',
            'fontColor',
            'fontBackgroundColor',
            '|',
            'link',
            'imageUpload',
            'htmlSourceCode',
            'mediaEmbed',
            'insertTable',
            'alignment',
            '|',
            'bulletedList',
            'numberedList',
            'todoList',
            'outdent',
            'indent',
            '|',
            'blockQuote',
            'horizontalLine',
            'codeBlock',
            '|',
            'undo',
            'redo',
            'fullscreen',
          ],
          viewportTopOffset: 50,
        },
        codeBlock: {
          languages: [
            // Do not render the CSS class for the plain text code blocks.
            { language: 'html', label: 'HTML', class: 'html' },
            { language: 'css', label: 'CSS', class: 'css' },
            {
              language: 'javascript',
              label: 'JavaScropt',
              class: 'javascript',
            },
            { language: 'php', label: 'PHP', class: 'php' },
            {
              language: 'structure',
              label: 'Structure',
              class: 'structure',
            },
          ],
        },
        fontColor: {
          columns: 5,
          colors: [
            {
              color: 'hsl(0, 0%, 0%)',
              label: 'Black',
            },
            {
              color: 'hsl(0, 0%, 30%)',
              label: 'Dim grey',
            },
            {
              color: 'hsl(0, 0%, 60%)',
              label: 'Grey',
            },
            {
              color: 'hsl(0, 0%, 90%)',
              label: 'Light grey',
            },
            {
              color: 'hsl(0, 0%, 100%)',
              label: 'White',
              hasBorder: true,
            },
            {
              color: 'hsl(0, 75%, 60%)',
              label: 'Red',
            },
            {
              color: 'hsl(30, 75%, 60%)',
              label: 'Orange',
            },
            {
              color: 'hsl(60, 75%, 60%)',
              label: 'Yellow',
            },
            {
              color: 'hsl(90, 75%, 60%)',
              label: 'Light green',
            },
            {
              color: 'hsl(120, 75%, 60%)',
              label: 'Green',
            },
            {
              color: 'hsl(150, 75%, 60%)',
              label: 'Aquamarine',
            },
            {
              color: 'hsl(180, 75%, 60%)',
              label: 'Turquoise',
            },
            {
              color: 'hsl(210, 75%, 60%)',
              label: 'Light blue',
            },
            {
              color: 'hsl(240, 75%, 60%)',
              label: 'Blue',
            },
            {
              color: 'hsl(270, 75%, 60%)',
              label: 'Purple',
            },
          ],
        },
        indentBlock: {
          offset: 1,
          unit: 'em',
        },
        simpleUpload: {
          // The URL that the images are uploaded to.
          uploadUrl: $('base').attr('href') + 'api/pic/CKUpload',

          // Headers sent along with the XMLHttpRequest to the upload server.
          headers: {
            'X-CSRF-TOKEN': 'CSFR-Token',
            Authorization: 'Bearer <JSON Web Token>',
          },
        },
        image: {
          // You need to configure the image toolbar, too, so it uses the new style buttons.
          toolbar: [
            'imageTextAlternative',
            '|',
            'imageStyle:alignLeft',
            // 'imageStyle:full',
            'imageStyle:alignRight',
            '|',
            'linkimage',
          ],

          styles: [
            // This option is equal to a situation where no style is applied.
            'full',

            // This represents an image aligned to the left.
            'alignLeft',

            // This represents an image aligned to the right.
            'alignRight',
          ],
          upload: {
            types: ['jpeg', 'gif', 'png'],
          },
          // resizeUnit: 'px'
        },
        table: {
          contentToolbar: [
            'tableColumn',
            'tableRow',
            'mergeTableCells',
          ],
        },
        alignment: {
          options: ['left', 'right', 'center'],
        },
        fontSize: {
          options: ['default', 12, 16, 20, 24, 28, 32],
        },
        fontBackgroundColor: {
          columns: 5,
          colors: [
            {
              color: 'hsl(0, 0%, 0%)',
              label: 'Black',
            },
            {
              color: 'hsl(0, 0%, 30%)',
              label: 'Dim grey',
            },
            {
              color: 'hsl(0, 0%, 60%)',
              label: 'Grey',
            },
            {
              color: 'hsl(0, 0%, 90%)',
              label: 'Light grey',
            },
            {
              color: 'hsl(0, 0%, 100%)',
              label: 'White',
              hasBorder: true,
            },
            {
              color: 'hsl(0, 75%, 60%)',
              label: 'Red',
            },
            {
              color: 'hsl(30, 75%, 60%)',
              label: 'Orange',
            },
            {
              color: 'hsl(60, 75%, 60%)',
              label: 'Yellow',
            },
            {
              color: 'hsl(90, 75%, 60%)',
              label: 'Light green',
            },
            {
              color: 'hsl(120, 75%, 60%)',
              label: 'Green',
            },
            {
              color: 'hsl(150, 75%, 60%)',
              label: 'Aquamarine',
            },
            {
              color: 'hsl(180, 75%, 60%)',
              label: 'Turquoise',
            },
            {
              color: 'hsl(210, 75%, 60%)',
              label: 'Light blue',
            },
            {
              color: 'hsl(240, 75%, 60%)',
              label: 'Blue',
            },
            {
              color: 'hsl(270, 75%, 60%)',
              label: 'Purple',
            },
          ],
        },
        typing: {
          transformations: {
            include: [
              'trademark',
              'registeredTrademark',
              'copyright',
              {
                from: 'Yi',
                to: 'Yi Yang',
              },
            ],
          },
        },
        mediaEmbed: {
          // configuration...
        },
        link: {
          // addTargetToExternalLinks: true,
          decorators: {
            openInNewTab: {
              mode: 'manual',
              label: 'Open in a new tab',
              attributes: {
                target: '_blank',
                rel: 'noopener noreferrer',
              },
            },
          },
        },
        language: 'zh',
      },
      ckeditorTextarea: '',
      CKEditorToolbarItems: [],
      picObjects: {},
    };
  },
  watch: {
    ckeditorTextarea: {
      handler: function(newVal) {
        let imgUrls = Array.from(
            new DOMParser()
                .parseFromString(this.ckeditorTextarea, 'text/html')
                .querySelectorAll('img'),
        ).map((img) => img.getAttribute('src'));

        let picObjects = [];

        for (let i = 0; i < imgUrls.length; i++) {
          if (!imgUrls[i]) {
            continue;
          }

          let ImgURL = new URL(imgUrls[i]);
          if (ImgURL.pathname) {
            ImgURL = ImgURL.pathname.split('/pic/')[1];

            if (ImgURL) {
              let ImgURLTmp = ImgURL.split('-');
              let id = parseInt(ImgURLTmp[0].replace(/\//g, ''));
              let md5 = ImgURLTmp[1].split('.')[0];
              picObjects.push({
                id,
                md5,
              });
            }
          }
        }

        this.picObjects = picObjects;
        this.$emit('input', newVal);
        this.$emit('update:textPic', picObjects);
      },
      deep: true
    },
    value(value) {
      this.ckeditorTextarea = value;
    },
    readonly(value) {
      this.isReadOnly = value;
    },
  },
  methods: {},
  created: function () {
    this.ckeditorTextarea = this.value;
  },
  mounted: function () {
    if (!this.inputNamePic) {
      console.warn('請填寫 inputNamePic 參數');
    }
  },
};
</script>

<style lang="scss">
.ckediorContainer{
  padding-bottom: 1rem;
  width: 100%;
}
.ck.ck-content.ck-editor__editable{
  min-height: 100px;
  max-height: 500px;
  ul, ol {
    padding-left: 2rem;
  }
}
</style>
